import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Google Analytics 跟踪ID
 * 从环境变量获取，如果没有则使用空字符串
 */
const GA_TRACKING_ID = import.meta.env.VITE_GA_TRACKING_ID || '';

/**
 * 初始化 Google Analytics
 * 如果没有配置跟踪ID，则不会加载
 */
export const initGA = () => {
  if (!GA_TRACKING_ID || import.meta.env.DEV) {
    console.log('GA not initialized: No tracking ID or in development mode');
    return;
  }

  // 加载 Google Analytics 脚本
  const script1 = document.createElement('script');
  script1.async = true;
  script1.src = `https://www.googletagmanager.com/gtag/js?id=${GA_TRACKING_ID}`;
  document.head.appendChild(script1);

  // 初始化 gtag
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  gtag('js', new Date());
  gtag('config', GA_TRACKING_ID, {
    send_page_view: false // 禁用自动页面浏览，手动跟踪
  });

  window.gtag = gtag;
};

/**
 * 发送页面浏览事件
 * @param {string} path - 页面路径
 * @param {string} [title] - 页面标题
 */
export const pageview = (path, title) => {
  if (!GA_TRACKING_ID || !window.gtag) return;

  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title || document.title,
    page_location: window.location.href
  });
};

/**
 * 发送自定义事件
 * @param {string} action - 事件动作
 * @param {string} category - 事件类别
 * @param {string} [label] - 事件标签
 * @param {number} [value] - 事件值
 */
export const event = (action, category, label, value) => {
  if (!GA_TRACKING_ID || !window.gtag) return;

  window.gtag('event', action, {
    event_category: category,
    event_label: label,
    value
  });
};

/**
 * 用户行为事件
 */
export const userEvents = {
  /** 登录事件 */
  login: (method) => event('login', 'user', method),
  /** 注册事件 */
  register: (method) => event('sign_up', 'user', method),
  /** 登出事件 */
  logout: () => event('logout', 'user'),
  /** 页面访问事件 */
  pageVisit: (pageName) => event('page_visit', 'engagement', pageName)
};

/**
 * 电商事件
 */
export const ecommerceEvents = {
  /** 加入购物车 */
  addToCart: (item) => event('add_to_cart', 'ecommerce', item.name, item.price),
  /** 开始结账 */
  beginCheckout: () => event('begin_checkout', 'ecommerce'),
  /** 购买完成 */
  purchase: (value) => event('purchase', 'ecommerce', null, value)
};

/**
 * 游戏事件
 */
export const gameEvents = {
  /** 开始游戏 */
  gameStart: (gameMode) => event('game_start', 'game', gameMode),
  /** 完成关卡 */
  levelComplete: (level) => event('level_complete', 'game', `Level ${level}`),
  /** 选择角色 */
  characterSelect: (character) => event('select_content', 'game', character)
};

/**
 * 路由跟踪组件
 * 自动跟踪页面浏览
 */
export const RouteTracker = () => {
  const location = useLocation();

  useEffect(() => {
    pageview(location.pathname + location.search);
  }, [location]);

  return null;
};

/**
 * 分析工具Hook
 * 用于在组件中使用分析功能
 */
export const useAnalytics = () => {
  return {
    pageview,
    event,
    userEvents,
    ecommerceEvents,
    gameEvents
  };
};

export default {
  initGA,
  pageview,
  event,
  userEvents,
  ecommerceEvents,
  gameEvents,
  RouteTracker,
  useAnalytics
};
